import { useState } from 'react';

import { TCreateProjectInputSchema } from '@/console/features/projects/components/create-project/create-project.types';
import { useDefineAssetTags } from '@/shared/features/tags/hooks/use-define-asset-tags/use-define-asset-tags';
import { TAssetOperationTypeEnum } from '@/shared/hooks/use-assets-app-sync-events/use-assets-app-sync-events.types';
import { useInvalidateProjectTags } from '@/shared/hooks/use-invalidate-asset-tags/hooks/use-invalidate-project-tags';
import { useMultipartUpload } from '@/shared/hooks/use-multipart-upload/use-multipart-upload';
import {
  useCompleteProjectMultipartUploadMutation,
  useCreateProjectMutation,
} from '@/shared/redux/api/assets/projects/projects-api';
import { AssetTypeEnum } from '@/shared/types/types.generated';
import { awaitAssetEvent } from '@/shared/utils/asset-event-wait/asset-event-wait';
import { ASSET_EVENT_TIMEOUT_MS } from '@/shared/utils/asset-event-wait/asset-event-wait.constants';

export const useCreateProject = () => {
  const [_createProject] = useCreateProjectMutation();
  const [completeUpload] = useCompleteProjectMultipartUploadMutation();
  const { uploadFile, calculatePartsCount } = useMultipartUpload();
  const defineAssetTags = useDefineAssetTags();
  const { invalidateProjectVersionTags } = useInvalidateProjectTags();
  const [uploadPercentage, setUploadPercentage] = useState<number>(0);

  const createProject = async (input: TCreateProjectInputSchema) => {
    const { file, tags, vaultId: _, groupId, ...rest } = input;
    const project = {
      ...rest,
      groupId: groupId ? groupId : undefined,
    };
    const response = await _createProject({
      project: {
        ...project,
        parts: calculatePartsCount(file.size),
        fileSize: file.size,
      },
    }).unwrap();

    const { projectId, versionId, uploadId } = response.createProject;

    // The version row is written by the S3 listener, not by the upload: until its
    // event lands, the list row shows the project with no version.
    const { eventReceived } = await awaitAssetEvent(
      { assetType: AssetTypeEnum.ProjectVersion, assetId: versionId },
      async () => {
        const parts = await uploadFile(
          response.createProject.uploadUrls,
          file,
          ({ percentage }) => setUploadPercentage(percentage),
        );

        if (tags && tags.length > 0) {
          await defineAssetTags({
            tags,
            assetId: projectId,
            assetType: AssetTypeEnum.Project,
          });
        }

        await completeUpload({
          projectId,
          versionId,
          uploadId,
          completeUploadInput: {
            fileName: file.name,
            parts,
          },
        }).unwrap();

        // The wait that follows is not an upload; stop claiming a percentage.
        setUploadPercentage(0);
      },
    );

    if (!eventReceived) {
      console.warn(
        `No ProjectVersion event for ${versionId} within ${ASSET_EVENT_TIMEOUT_MS}ms; refetching the projects list instead.`,
      );
      invalidateProjectVersionTags(
        projectId,
        TAssetOperationTypeEnum.AssetCreated,
      );
    }

    return response.createProject;
  };

  return {
    createProject,
    uploadPercentage,
  };
};
